import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { 
  RefreshCw, 
  TrendingUp, 
  TrendingDown, 
  Globe,
  Gem,
  Bitcoin,
  Newspaper,
  DollarSign,
  Brain,
  Zap,
  ArrowUpDown,
  ExternalLink,
  Sparkles,
  Target,
  HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const MARKET_TABS = [
  { id: "usa", name: "USA Predictions", icon: Zap },
  { id: "options-advisor", name: "Should I Buy?", icon: Target },
  { id: "crypto-scanner", name: "Crypto Scanner", icon: Sparkles },
  { id: "ai", name: "AI Analysis", icon: Brain },
  { id: "currencies", name: "Currencies", icon: DollarSign },
  { id: "indices", name: "Global Indices", icon: Globe },
  { id: "commodities", name: "Commodities", icon: Gem },
  { id: "crypto", name: "Crypto Prices", icon: Bitcoin },
  { id: "options", name: "Options Ask", icon: Target },
];

// 🎯 OPTIONS ADVISOR COMPONENT - "Should I Buy This Option?"
const OptionsAdvisor = () => {
  const [symbol, setSymbol] = useState("");
  const [strike, setStrike] = useState("");
  const [expiry, setExpiry] = useState("");
  const [optionType, setOptionType] = useState("CALL");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const analyzeOption = async () => {
    if (!symbol || !strike || !expiry) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    setResult(null);
    
    try {
      const response = await api.get(`/options/should-i-buy`, {
        params: {
          symbol: symbol.toUpperCase(),
          strike: parseFloat(strike),
          expiry: expiry,
          type: optionType
        }
      });
      setResult(response.data);
    } catch (error) {
      toast.error("Failed to analyze option");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getRecommendationStyle = (rec) => {
    switch(rec) {
      case 'STRONG BUY': return 'bg-green-500 text-white animate-pulse';
      case 'BUY': return 'bg-emerald-500 text-white';
      case 'HOLD/WAIT': return 'bg-yellow-500 text-black';
      case "DON'T BUY": return 'bg-orange-500 text-white';
      case 'AVOID': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  return (
    <Card className="bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-red-500/10 border-amber-500/20">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <Target className="w-6 h-6 text-amber-400" />
          🎯 SHOULD I BUY THIS OPTION?
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Get AI-powered recommendation using Whale Flow + Technicals + News + Trump Factor
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Input Form */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="space-y-2">
            <Label htmlFor="symbol">Symbol</Label>
            <Input
              id="symbol"
              placeholder="AAPL"
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
              className="bg-background/50"
              data-testid="options-symbol-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="strike">Strike Price</Label>
            <Input
              id="strike"
              type="number"
              placeholder="180.00"
              value={strike}
              onChange={(e) => setStrike(e.target.value)}
              className="bg-background/50"
              data-testid="options-strike-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="expiry">Expiry Date</Label>
            <Input
              id="expiry"
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="bg-background/50"
              data-testid="options-expiry-input"
            />
          </div>
          <div className="space-y-2">
            <Label>Type</Label>
            <div className="flex gap-2">
              <Button
                variant={optionType === "CALL" ? "default" : "outline"}
                onClick={() => setOptionType("CALL")}
                className={optionType === "CALL" ? "bg-green-600 hover:bg-green-700" : ""}
                size="sm"
                data-testid="options-call-btn"
              >
                📈 CALL
              </Button>
              <Button
                variant={optionType === "PUT" ? "default" : "outline"}
                onClick={() => setOptionType("PUT")}
                className={optionType === "PUT" ? "bg-red-600 hover:bg-red-700" : ""}
                size="sm"
                data-testid="options-put-btn"
              >
                📉 PUT
              </Button>
            </div>
          </div>
          <div className="space-y-2 flex items-end">
            <Button
              onClick={analyzeOption}
              disabled={loading}
              className="w-full bg-amber-600 hover:bg-amber-700"
              data-testid="analyze-option-btn"
            >
              {loading ? "Analyzing..." : "🎯 ANALYZE"}
            </Button>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="space-y-4 p-4 rounded-lg border border-amber-500/30 bg-background/50">
            {/* Main Recommendation */}
            <div className="text-center space-y-2">
              <Badge className={`text-2xl px-6 py-2 ${getRecommendationStyle(result.recommendation)}`}>
                {result.recommendation}
              </Badge>
              <p className="text-lg font-semibold">{result.verdict}</p>
              <div className="flex justify-center gap-4 text-sm">
                <span>Confidence: <strong>{result.confidence}%</strong></span>
                <span>Profit Probability: <strong>{result.probability_of_profit}%</strong></span>
              </div>
            </div>

            {/* Option Details */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="p-2 rounded bg-muted/50">
                <p className="text-muted-foreground">Current Price</p>
                <p className="font-bold">${result.current_price?.toFixed(2)}</p>
              </div>
              <div className="p-2 rounded bg-muted/50">
                <p className="text-muted-foreground">Strike</p>
                <p className="font-bold">${result.strike_price}</p>
              </div>
              <div className="p-2 rounded bg-muted/50">
                <p className="text-muted-foreground">Days to Expiry</p>
                <p className="font-bold">{result.days_to_expiry} days</p>
              </div>
              <div className="p-2 rounded bg-muted/50">
                <p className="text-muted-foreground">Moneyness</p>
                <p className={`font-bold ${result.in_the_money ? 'text-green-400' : 'text-yellow-400'}`}>
                  {result.in_the_money ? 'ITM' : 'OTM'} ({result.moneyness?.toFixed(1)}%)
                </p>
              </div>
            </div>

            {/* Score Breakdown */}
            <div className="space-y-2">
              <p className="font-semibold text-sm">Score Breakdown:</p>
              <div className="grid grid-cols-5 gap-2 text-xs">
                {Object.entries(result.scores?.breakdown || {}).map(([key, value]) => (
                  <div key={key} className={`p-2 rounded text-center ${value > 0 ? 'bg-green-500/20' : value < 0 ? 'bg-red-500/20' : 'bg-muted/50'}`}>
                    <p className="text-muted-foreground capitalize">{key}</p>
                    <p className={`font-bold ${value > 0 ? 'text-green-400' : value < 0 ? 'text-red-400' : ''}`}>
                      {value > 0 ? '+' : ''}{value}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Factors & Risks */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <p className="font-semibold text-sm text-green-400">✅ Supporting Factors:</p>
                {result.factors?.map((f, i) => (
                  <p key={i} className="text-xs text-muted-foreground">{f}</p>
                ))}
                {(!result.factors || result.factors.length === 0) && (
                  <p className="text-xs text-muted-foreground">No strong supporting factors</p>
                )}
              </div>
              <div className="space-y-2">
                <p className="font-semibold text-sm text-red-400">⚠️ Risks:</p>
                {result.risks?.map((r, i) => (
                  <p key={i} className="text-xs text-muted-foreground">{r}</p>
                ))}
                {(!result.risks || result.risks.length === 0) && (
                  <p className="text-xs text-muted-foreground">No major risks identified</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Instructions */}
        {!result && (
          <div className="text-center text-muted-foreground text-sm p-4 border border-dashed border-muted-foreground/30 rounded-lg">
            <HelpCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p>Enter an option contract to get AI-powered analysis</p>
            <p className="text-xs mt-1">Combines: 🐋 Whale Flow (35%) + 📊 Technicals (25%) + 🔴 Trump Factor (20%) + 📈 Pre-Market (15%)</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Crypto Signal Card Component
const CryptoSignalCard = ({ coin, signalType }) => {
  const getSignalColor = () => {
    switch(signalType) {
      case 'STRONG_BUY': return 'border-green-500/50 bg-green-500/10';
      case 'BUY': return 'border-emerald-500/30 bg-emerald-500/5';
      case 'SELL': return 'border-red-500/30 bg-red-500/5';
      default: return 'border-yellow-500/30 bg-yellow-500/5';
    }
  };

  const getCoinIcon = (symbol) => {
    const icons = { 'BTC': '₿', 'ETH': 'Ξ', 'SOL': '◎', 'XRP': '✕', 'DOGE': 'Ð', 'ADA': '₳' };
    return icons[symbol] || '●';
  };

  return (
    <Card className={`border ${getSignalColor()} transition-all hover:scale-[1.02]`} data-testid={`crypto-signal-${coin.symbol}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl font-bold ${
              coin.symbol === 'BTC' ? 'bg-orange-500/20 text-orange-400' :
              coin.symbol === 'ETH' ? 'bg-blue-500/20 text-blue-400' :
              coin.symbol === 'SOL' ? 'bg-purple-500/20 text-purple-400' :
              coin.symbol === 'XRP' ? 'bg-gray-500/20 text-gray-400' :
              coin.symbol === 'DOGE' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-cyan-500/20 text-cyan-400'
            }`}>
              {getCoinIcon(coin.symbol)}
            </div>
            <div>
              <p className="font-bold text-lg">{coin.symbol}</p>
              <p className="text-xs text-muted-foreground">{coin.source}</p>
            </div>
          </div>
          <Badge className={`${
            signalType === 'STRONG_BUY' ? 'bg-green-500 animate-pulse' :
            signalType === 'BUY' ? 'bg-emerald-500' :
            signalType === 'SELL' ? 'bg-red-500' :
            'bg-yellow-500'
          } text-white`}>
            {signalType.replace('_', ' ')}
          </Badge>
        </div>

        <p className="font-data font-bold text-2xl mb-2">
          ${coin.current_price?.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
        </p>

        {/* Key Indicators */}
        <div className="grid grid-cols-3 gap-2 mb-3 text-xs">
          <div className="p-2 rounded bg-muted/50">
            <p className="text-muted-foreground">RSI</p>
            <p className={`font-bold ${
              coin.indicators?.rsi < 30 ? 'text-green-400' : 
              coin.indicators?.rsi > 70 ? 'text-red-400' : 'text-white'
            }`}>
              {coin.indicators?.rsi?.toFixed(1)}
            </p>
          </div>
          <div className="p-2 rounded bg-muted/50">
            <p className="text-muted-foreground">MACD</p>
            <p className={`font-bold ${coin.indicators?.macd > 0 ? 'text-green-400' : 'text-red-400'}`}>
              {coin.indicators?.macd > 0 ? '+' : ''}{coin.indicators?.macd?.toFixed(0)}
            </p>
          </div>
          <div className="p-2 rounded bg-muted/50">
            <p className="text-muted-foreground">Vol</p>
            <p className={`font-bold ${
              coin.volume?.trend === 'HIGH' ? 'text-green-400' : 
              coin.volume?.trend === 'LOW' ? 'text-red-400' : 'text-yellow-400'
            }`}>
              {coin.volume?.trend || 'N/A'}
            </p>
          </div>
        </div>

        {/* Patterns */}
        {coin.patterns?.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-semibold text-muted-foreground">SIGNALS:</p>
            {coin.patterns.slice(0, 3).map((pattern, idx) => (
              <p key={idx} className="text-xs text-muted-foreground truncate">{pattern}</p>
            ))}
          </div>
        )}

        {/* Breakout Badge */}
        {coin.breakout?.detected && (
          <Badge className="mt-2 bg-purple-500/20 text-purple-400 border border-purple-500/30">
            ⚡ {coin.breakout.type?.replace(/_/g, ' ')}
          </Badge>
        )}
      </CardContent>
    </Card>
  );
};

export default function Markets() {
  const [marketData, setMarketData] = useState(null);
  const [currencies, setCurrencies] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [usaPredictions, setUsaPredictions] = useState(null);
  const [predictionAccuracy, setPredictionAccuracy] = useState(null);
  const [cryptoScanner, setCryptoScanner] = useState(null);
  const [cryptoScannerLoading, setCryptoScannerLoading] = useState(false);
  const [predictionLogs, setPredictionLogs] = useState(null);
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [usaLoading, setUsaLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("usa");

  const fetchMarketData = useCallback(async () => {
    try {
      const marketsRes = await api.get("/markets/overview");
      setMarketData(marketsRes.data);
    } catch (error) {
      console.error("Error fetching market data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCurrencies = useCallback(async () => {
    try {
      const response = await api.get("/markets/currencies");
      setCurrencies(response.data);
    } catch (error) {
      console.error("Error fetching currencies:", error);
    }
  }, []);

  const fetchAiAnalysis = useCallback(async () => {
    setAiLoading(true);
    try {
      const response = await api.get("/markets/ai-analysis");
      setAiAnalysis(response.data);
    } catch (error) {
      console.error("Error fetching AI analysis:", error);
    } finally {
      setAiLoading(false);
    }
  }, []);

  const fetchUsaPredictions = useCallback(async () => {
    setUsaLoading(true);
    try {
      const [predictionsRes, accuracyRes, logsRes] = await Promise.all([
        api.get("/market-prediction/indices"),
        api.get("/market-prediction/accuracy/history"),
        api.get("/market-prediction/logs?limit=50")
      ]);
      setUsaPredictions(predictionsRes.data);
      setPredictionAccuracy(accuracyRes.data);
      setPredictionLogs(logsRes.data);
    } catch (error) {
      console.error("Error fetching USA predictions:", error);
    } finally {
      setUsaLoading(false);
    }
  }, []);

  const fetchCryptoScanner = useCallback(async () => {
    setCryptoScannerLoading(true);
    try {
      const response = await api.get("/crypto/scanner");
      setCryptoScanner(response.data);
    } catch (error) {
      console.error("Error fetching crypto scanner:", error);
    } finally {
      setCryptoScannerLoading(false);
    }
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchMarketData(), fetchCurrencies(), fetchUsaPredictions()]);
    setRefreshing(false);
    toast.success("Market data updated");
  };

  useEffect(() => {
    fetchMarketData();
    fetchCurrencies();
    fetchAiAnalysis();
    fetchUsaPredictions();
    fetchCryptoScanner();
  }, [fetchMarketData, fetchCurrencies, fetchAiAnalysis, fetchUsaPredictions, fetchCryptoScanner]);

  const getChangeColor = (change) => {
    if (change > 0) return "text-green-400";
    if (change < 0) return "text-red-400";
    return "text-muted-foreground";
  };

  const getChangeBg = (change) => {
    if (change > 0) return "bg-green-500/20 border-green-500/30";
    if (change < 0) return "bg-red-500/20 border-red-500/30";
    return "bg-muted border-border";
  };

  const getFlagEmoji = (country) => {
    const flags = {
      "USA": "🇺🇸", "Japan": "🇯🇵", "China": "🇨🇳", "Hong Kong": "🇭🇰",
      "India": "🇮🇳", "UK": "🇬🇧", "Germany": "🇩🇪", "France": "🇫🇷",
      "Europe": "🇪🇺", "Australia": "🇦🇺",
    };
    return flags[country] || "🌍";
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="markets-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="h-24 skeleton rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="markets-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-yellow-400" />
            MARKET COMMAND CENTER
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            AI-powered analysis • Currencies • Global indices • Real-time data
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="gap-1">
            Updated: {marketData?.updated_at ? new Date(marketData.updated_at).toLocaleTimeString() : 'N/A'}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
            className="gap-2"
            data-testid="refresh-markets-btn"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-card border border-border h-auto flex-wrap justify-start gap-1 p-1">
          {MARKET_TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <TabsTrigger 
                key={tab.id} 
                value={tab.id}
                className="font-heading gap-2 data-[state=active]:bg-white data-[state=active]:text-black"
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{tab.name}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* 🚀 USA INDICES PREDICTIONS TAB */}
        <TabsContent value="usa" className="mt-6 space-y-6">
          <Card className="bg-gradient-to-br from-blue-500/10 via-indigo-500/10 to-purple-500/10 border-blue-500/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Zap className="w-6 h-6 text-yellow-400 animate-pulse" />
                  SUPERCHARGED MARKET PREDICTIONS
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchUsaPredictions}
                  disabled={usaLoading}
                  className="border-blue-500/30 hover:bg-blue-500/20"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${usaLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                AI-powered predictions using technicals, global markets, news sentiment & more
              </p>
            </CardHeader>
            <CardContent>
              {usaLoading ? (
                <div className="space-y-4">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-24 bg-muted/50 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : usaPredictions ? (
                <div className="space-y-6">
                  {/* Overall Sentiment */}
                  <div className={`p-4 rounded-lg border ${
                    usaPredictions.overall_sentiment === 'BULLISH' ? 'bg-green-500/10 border-green-500/30' :
                    usaPredictions.overall_sentiment === 'BEARISH' ? 'bg-red-500/10 border-red-500/30' :
                    'bg-yellow-500/10 border-yellow-500/30'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                          usaPredictions.overall_sentiment === 'BULLISH' ? 'bg-green-500/20' :
                          usaPredictions.overall_sentiment === 'BEARISH' ? 'bg-red-500/20' :
                          'bg-yellow-500/20'
                        }`}>
                          {usaPredictions.overall_sentiment === 'BULLISH' ? (
                            <TrendingUp className="w-6 h-6 text-green-400" />
                          ) : usaPredictions.overall_sentiment === 'BEARISH' ? (
                            <TrendingDown className="w-6 h-6 text-red-400" />
                          ) : (
                            <ArrowUpDown className="w-6 h-6 text-yellow-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-lg font-bold">Overall Market: {usaPredictions.overall_sentiment}</p>
                          <p className="text-sm text-muted-foreground">
                            Aggregate Score: {usaPredictions.average_score > 0 ? '+' : ''}{usaPredictions.average_score?.toFixed(1)}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        Updated: {new Date(usaPredictions.generated_at).toLocaleTimeString()}
                      </Badge>
                    </div>
                  </div>

                  {/* Index Predictions */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {usaPredictions.predictions?.map((pred) => (
                      <Card key={pred.symbol} className={`border ${
                        pred.prediction?.includes('UP') ? 'border-green-500/30 bg-green-500/5' :
                        pred.prediction?.includes('DOWN') ? 'border-red-500/30 bg-red-500/5' :
                        'border-yellow-500/30 bg-yellow-500/5'
                      }`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">{pred.direction_emoji}</span>
                              <div>
                                <p className="font-bold text-lg">{pred.short_name || pred.name}</p>
                                <p className="text-xs text-muted-foreground">${pred.current_price?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                              </div>
                            </div>
                            <Badge className={`${
                              pred.prediction?.includes('UP') ? 'bg-green-500' :
                              pred.prediction?.includes('DOWN') ? 'bg-red-500' :
                              'bg-yellow-500'
                            } text-white`}>
                              {pred.prediction}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-muted-foreground">Confidence</span>
                            <span className="font-bold">{pred.confidence}%</span>
                          </div>
                          
                          <div className="w-full bg-muted rounded-full h-2 mb-3">
                            <div 
                              className={`h-2 rounded-full ${
                                pred.prediction?.includes('UP') ? 'bg-green-500' :
                                pred.prediction?.includes('DOWN') ? 'bg-red-500' :
                                'bg-yellow-500'
                              }`}
                              style={{ width: `${pred.confidence}%` }}
                            />
                          </div>
                          
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-muted-foreground">KEY SIGNALS:</p>
                            {pred.signals?.slice(0, 3).map((signal, idx) => (
                              <p key={idx} className="text-xs text-muted-foreground">{signal}</p>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Prediction Accuracy */}
                  {predictionAccuracy && (
                    <Card className="border-purple-500/20 bg-purple-500/5">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Brain className="w-4 h-4 text-purple-400" />
                          🧠 SELF-LEARNING ENGINE
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <p className="text-2xl font-bold text-purple-400">
                              {predictionAccuracy.overall_accuracy || 0}%
                            </p>
                            <p className="text-xs text-muted-foreground">Overall Accuracy</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-blue-400">
                              {predictionAccuracy.total_predictions || 0}
                            </p>
                            <p className="text-xs text-muted-foreground">Predictions Made</p>
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-green-400">
                              {predictionAccuracy.recent_7d_accuracy || 0}%
                            </p>
                            <p className="text-xs text-muted-foreground">Last 7 Days</p>
                          </div>
                        </div>
                        <p className="text-xs text-center mt-3 text-muted-foreground">
                          {predictionAccuracy.learning_status}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Prediction Logs / Success Tracker */}
                  {predictionLogs?.logs?.length > 0 && (
                    <Card className="border-cyan-500/20 bg-cyan-500/5">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-cyan-400" />
                          📜 PREDICTION HISTORY
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ScrollArea className="h-[250px]">
                          <div className="space-y-2">
                            {predictionLogs.logs.slice(0, 20).map((log, idx) => (
                              <div 
                                key={log.prediction_id || idx} 
                                className={`p-3 rounded-lg border ${
                                  log.verified && log.accuracy === true ? 'bg-green-500/10 border-green-500/30' :
                                  log.verified && log.accuracy === false ? 'bg-red-500/10 border-red-500/30' :
                                  'bg-muted/30 border-border'
                                }`}
                                data-testid={`prediction-log-${idx}`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">
                                      {log.prediction?.includes('UP') ? '📈' : 
                                       log.prediction?.includes('DOWN') ? '📉' : '↔️'}
                                    </span>
                                    <div>
                                      <p className="font-bold text-sm">{log.name}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {new Date(log.predicted_at).toLocaleString()}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <Badge className={`text-xs ${
                                      log.prediction?.includes('UP') ? 'bg-green-500/20 text-green-400' :
                                      log.prediction?.includes('DOWN') ? 'bg-red-500/20 text-red-400' :
                                      'bg-yellow-500/20 text-yellow-400'
                                    }`}>
                                      {log.prediction}
                                    </Badge>
                                    <p className="text-xs text-muted-foreground mt-1">
                                      {log.confidence}% conf • Score: {log.total_score?.toFixed(1)}
                                    </p>
                                  </div>
                                </div>
                                {log.verified && (
                                  <div className="mt-2 pt-2 border-t border-border/50">
                                    <span className={`text-xs font-bold ${
                                      log.accuracy === true ? 'text-green-400' : 'text-red-400'
                                    }`}>
                                      {log.accuracy === true ? '✅ CORRECT' : '❌ INCORRECT'}
                                      {log.actual_result && ` (Actual: ${log.actual_result})`}
                                    </span>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </CardContent>
                    </Card>
                  )}

                  {/* Factors Legend */}
                  <div className="text-xs text-muted-foreground p-3 bg-muted/30 rounded-lg">
                    <p className="font-semibold mb-1">📊 Prediction Factors (6 Total):</p>
                    <p>• Technical Analysis (30%) - RSI, MACD, Moving Averages</p>
                    <p>• News Sentiment (20%) - Fed news, market headlines</p>
                    <p>• Global Markets (20%) - Europe & Asia correlation</p>
                    <p>• Market Indicators (10%) - VIX, Treasury yields</p>
                    <p>• 🔴 Trump Factor (10%) - Presidential news & policy impact</p>
                    <p>• 🏛️ Institutional Flow (10%) - Smart money / hedge fund sentiment</p>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground">No predictions available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 🪙 CRYPTO SCANNER TAB */}
        <TabsContent value="crypto-scanner" className="mt-6 space-y-6" data-testid="crypto-scanner-tab">
          <Card className="bg-gradient-to-br from-orange-500/10 via-yellow-500/10 to-amber-500/10 border-orange-500/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-orange-400 animate-pulse" />
                  CRYPTO SCANNER - AI SIGNALS
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchCryptoScanner}
                  disabled={cryptoScannerLoading}
                  className="border-orange-500/30 hover:bg-orange-500/20"
                  data-testid="refresh-crypto-scanner-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${cryptoScannerLoading ? 'animate-spin' : ''}`} />
                  Scan
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Technical analysis signals for top cryptocurrencies • RSI, MACD, Bollinger Bands
              </p>
            </CardHeader>
            <CardContent>
              {cryptoScannerLoading ? (
                <div className="space-y-4">
                  {[1,2,3,4].map(i => (
                    <div key={i} className="h-32 bg-muted/50 rounded-lg animate-pulse" />
                  ))}
                </div>
              ) : cryptoScanner ? (
                <div className="space-y-6">
                  {/* Scanner Summary */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30 text-center">
                      <p className="text-2xl font-bold text-green-400">{cryptoScanner.summary?.strong_buys || 0}</p>
                      <p className="text-xs text-muted-foreground">Strong Buys</p>
                    </div>
                    <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-center">
                      <p className="text-2xl font-bold text-emerald-400">{cryptoScanner.summary?.buys || 0}</p>
                      <p className="text-xs text-muted-foreground">Buys</p>
                    </div>
                    <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                      <p className="text-2xl font-bold text-yellow-400">{cryptoScanner.summary?.holds || 0}</p>
                      <p className="text-xs text-muted-foreground">Holds</p>
                    </div>
                    <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-center">
                      <p className="text-2xl font-bold text-red-400">{cryptoScanner.summary?.sells || 0}</p>
                      <p className="text-xs text-muted-foreground">Sells</p>
                    </div>
                    <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30 text-center">
                      <p className="text-2xl font-bold text-purple-400">{cryptoScanner.summary?.breakouts || 0}</p>
                      <p className="text-xs text-muted-foreground">Breakouts</p>
                    </div>
                  </div>

                  {/* Strong Buys Section */}
                  {cryptoScanner.strong_buys?.length > 0 && (
                    <div>
                      <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                        <span className="text-green-400">🚀</span> STRONG BUY SIGNALS
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {cryptoScanner.strong_buys.map((coin) => (
                          <CryptoSignalCard key={coin.symbol} coin={coin} signalType="STRONG_BUY" />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Buy Signals Section */}
                  {cryptoScanner.buys?.length > 0 && (
                    <div>
                      <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                        <span className="text-emerald-400">📈</span> BUY SIGNALS
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {cryptoScanner.buys.map((coin) => (
                          <CryptoSignalCard key={coin.symbol} coin={coin} signalType="BUY" />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Hold Signals Section */}
                  {cryptoScanner.holds?.length > 0 && (
                    <div>
                      <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                        <span className="text-yellow-400">⏸️</span> HOLD SIGNALS
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {cryptoScanner.holds?.map((coin) => (
                          <CryptoSignalCard key={coin.symbol} coin={coin} signalType="HOLD" />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sell Signals */}
                  {cryptoScanner.sells?.length > 0 && (
                    <div>
                      <h3 className="font-heading text-lg mb-3 flex items-center gap-2">
                        <span className="text-red-400">📉</span> SELL SIGNALS
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {cryptoScanner.sells.map((coin) => (
                          <CryptoSignalCard key={coin.symbol} coin={coin} signalType="SELL" />
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Technical Legend */}
                  <div className="text-xs text-muted-foreground p-3 bg-muted/30 rounded-lg">
                    <p className="font-semibold mb-1">📊 Crypto Scanner Indicators:</p>
                    <p>• RSI (Relative Strength Index) - Overbought/Oversold detection</p>
                    <p>• MACD - Momentum and trend direction</p>
                    <p>• Bollinger Bands - Volatility and breakout detection</p>
                    <p>• Support/Resistance levels - Key price zones</p>
                  </div>
                </div>
              ) : (
                <p className="text-center text-muted-foreground">No crypto signals available</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* 🎯 OPTIONS ADVISOR - "Should I Buy This Option?" */}
        <TabsContent value="options-advisor" className="mt-6 space-y-6" data-testid="options-advisor-tab">
          <OptionsAdvisor />
        </TabsContent>

        {/* AI ANALYSIS TAB */}
        <TabsContent value="ai" className="mt-6 space-y-6">
          <Card className="bg-gradient-to-br from-purple-500/10 via-blue-500/10 to-cyan-500/10 border-purple-500/20">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading flex items-center gap-2">
                  <Brain className="w-6 h-6 text-purple-400" />
                  AI MARKET INTELLIGENCE
                </CardTitle>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={fetchAiAnalysis}
                  disabled={aiLoading}
                  className="gap-2"
                >
                  <Zap className={`w-4 h-4 ${aiLoading ? "animate-pulse" : ""}`} />
                  {aiLoading ? "Analyzing..." : "Get Fresh Analysis"}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {aiAnalysis?.analysis ? (
                <div className="space-y-4">
                  {/* Market Data Summary */}
                  {aiAnalysis.market_data && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                      <div className="p-3 bg-card/50 rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground">S&P 500</p>
                        <p className="text-xl font-bold">{aiAnalysis.market_data.sp500?.price?.toLocaleString()}</p>
                        <p className={`text-sm ${getChangeColor(aiAnalysis.market_data.sp500?.change_5d)}`}>
                          {aiAnalysis.market_data.sp500?.change_5d >= 0 ? '+' : ''}{aiAnalysis.market_data.sp500?.change_5d}% 5D
                        </p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground">VIX (Fear)</p>
                        <p className={`text-xl font-bold ${aiAnalysis.market_data.vix > 20 ? 'text-red-400' : 'text-green-400'}`}>
                          {aiAnalysis.market_data.vix}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {aiAnalysis.market_data.vix < 15 ? 'Low Fear' : aiAnalysis.market_data.vix < 25 ? 'Moderate' : 'High Fear'}
                        </p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground">Gold</p>
                        <p className="text-xl font-bold">${aiAnalysis.market_data.gold?.toLocaleString()}</p>
                      </div>
                      <div className="p-3 bg-card/50 rounded-lg border border-border">
                        <p className="text-xs text-muted-foreground">Bitcoin</p>
                        <p className="text-xl font-bold">${aiAnalysis.market_data.bitcoin?.price?.toLocaleString()}</p>
                        <p className={`text-sm ${getChangeColor(aiAnalysis.market_data.bitcoin?.change_24h)}`}>
                          {aiAnalysis.market_data.bitcoin?.change_24h >= 0 ? '+' : ''}{aiAnalysis.market_data.bitcoin?.change_24h?.toFixed(1)}% 24H
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* AI Analysis */}
                  <div className="prose prose-invert max-w-none">
                    <div className="p-6 bg-card/30 rounded-lg border border-purple-500/20 whitespace-pre-wrap text-white">
                      {aiAnalysis.analysis}
                    </div>
                  </div>
                  
                  <p className="text-xs text-muted-foreground text-right">
                    Generated: {new Date(aiAnalysis.generated_at).toLocaleString()}
                  </p>
                </div>
              ) : aiLoading ? (
                <div className="text-center py-12">
                  <Brain className="w-12 h-12 text-purple-400 mx-auto mb-4 animate-pulse" />
                  <p className="text-muted-foreground">AI is analyzing market conditions...</p>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Brain className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Click &quot;Get Fresh Analysis&quot; for AI-powered market insights</p>
                  <Button onClick={fetchAiAnalysis} className="gap-2">
                    <Zap className="w-4 h-4" />
                    Generate Analysis
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CURRENCIES TAB */}
        <TabsContent value="currencies" className="mt-6 space-y-6">
          <Card className="card-dark">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <DollarSign className="w-6 h-6 text-green-400" />
                USD EXCHANGE RATES
              </CardTitle>
              <p className="text-sm text-muted-foreground">Real-time currency rates vs US Dollar</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {currencies?.currencies?.map((currency, idx) => (
                  <div 
                    key={currency.pair}
                    className={`p-4 rounded-lg border ${getChangeBg(currency.change_day)} animate-slide-in`}
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-2xl">{currency.flag}</span>
                        <div>
                          <p className="font-bold text-white">{currency.pair}</p>
                          <p className="text-xs text-muted-foreground">1 USD =</p>
                        </div>
                      </div>
                      <ArrowUpDown className={`w-4 h-4 ${getChangeColor(currency.change_day)}`} />
                    </div>
                    <p className="text-3xl font-bold font-data text-white">
                      {currency.rate.toFixed(currency.quote === "JPY" ? 2 : 4)}
                    </p>
                    <p className="text-sm text-muted-foreground">{currency.quote}</p>
                    <div className="flex items-center gap-4 mt-2 pt-2 border-t border-border/50">
                      <div>
                        <p className="text-xs text-muted-foreground">24H</p>
                        <p className={`font-data font-bold ${getChangeColor(currency.change_day)}`}>
                          {currency.change_day >= 0 ? '+' : ''}{currency.change_day}%
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">7D</p>
                        <p className={`font-data font-bold ${getChangeColor(currency.change_week)}`}>
                          {currency.change_week >= 0 ? '+' : ''}{currency.change_week}%
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {currencies?.updated_at && (
                <p className="text-xs text-muted-foreground mt-4 text-right">
                  Updated: {new Date(currencies.updated_at).toLocaleString()}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* GLOBAL INDICES TAB */}
        <TabsContent value="indices" className="mt-6 space-y-6">
          {/* USA Indices */}
          <Card className="card-dark" data-testid="usa-indices">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2">
                <span className="text-2xl">🇺🇸</span>
                USA INDICES
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {marketData?.indices?.usa?.map((index, idx) => (
                  <div 
                    key={index.symbol}
                    className={`p-3 rounded-lg border ${getChangeBg(index.change)} animate-slide-in`}
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    <p className="text-xs text-muted-foreground">{index.name}</p>
                    <p className="font-data font-bold text-lg">{index.price?.toLocaleString()}</p>
                    <p className={`font-data text-sm ${getChangeColor(index.change)}`}>
                      {index.change >= 0 ? '▲' : '▼'} {Math.abs(index.change)?.toFixed(2)}%
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Asian Indices */}
          <Card className="card-dark" data-testid="asian-indices">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2">
                <span className="text-2xl">🌏</span>
                ASIAN INDICES
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {marketData?.indices?.asia?.map((index, idx) => (
                  <div 
                    key={index.symbol}
                    className={`p-3 rounded-lg border ${getChangeBg(index.change)} animate-slide-in`}
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    <div className="flex items-center gap-1 mb-1">
                      <span>{getFlagEmoji(index.country)}</span>
                      <p className="text-xs text-muted-foreground">{index.name}</p>
                    </div>
                    <p className="font-data font-bold text-lg">{index.price?.toLocaleString()}</p>
                    <p className={`font-data text-sm ${getChangeColor(index.change)}`}>
                      {index.change >= 0 ? '▲' : '▼'} {Math.abs(index.change)?.toFixed(2)}%
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* European Indices */}
          <Card className="card-dark" data-testid="european-indices">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2">
                <span className="text-2xl">🇪🇺</span>
                EUROPEAN INDICES
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {marketData?.indices?.europe?.map((index, idx) => (
                  <div 
                    key={index.symbol}
                    className={`p-3 rounded-lg border ${getChangeBg(index.change)} animate-slide-in`}
                    style={{ animationDelay: `${idx * 30}ms` }}
                  >
                    <div className="flex items-center gap-1 mb-1">
                      <span>{getFlagEmoji(index.country)}</span>
                      <p className="text-xs text-muted-foreground">{index.name}</p>
                    </div>
                    <p className="font-data font-bold text-lg">{index.price?.toLocaleString()}</p>
                    <p className={`font-data text-sm ${getChangeColor(index.change)}`}>
                      {index.change >= 0 ? '▲' : '▼'} {Math.abs(index.change)?.toFixed(2)}%
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* COMMODITIES TAB */}
        <TabsContent value="commodities" className="mt-6 space-y-6">
          {/* Precious Metals */}
          <Card className="card-dark" data-testid="precious-metals">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2">
                <Gem className="w-5 h-5 text-yellow-400" />
                PRECIOUS METALS
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {marketData?.commodities?.metals?.map((metal, idx) => (
                  <div 
                    key={metal.symbol}
                    className={`p-4 rounded-lg border ${getChangeBg(metal.change)} animate-slide-in`}
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        metal.symbol === 'GOLD' ? 'bg-yellow-500/20' :
                        metal.symbol === 'SILVER' ? 'bg-gray-400/20' :
                        metal.symbol === 'PLATINUM' ? 'bg-blue-300/20' : 'bg-orange-500/20'
                      }`}>
                        <Gem className={`w-5 h-5 ${
                          metal.symbol === 'GOLD' ? 'text-yellow-400' :
                          metal.symbol === 'SILVER' ? 'text-gray-300' :
                          metal.symbol === 'PLATINUM' ? 'text-blue-300' : 'text-orange-400'
                        }`} />
                      </div>
                      <div>
                        <p className="font-heading font-bold">{metal.symbol}</p>
                        <p className="text-xs text-muted-foreground">{metal.name}</p>
                      </div>
                    </div>
                    <p className="font-data font-bold text-2xl">${metal.price?.toFixed(2)}</p>
                    <p className={`font-data ${getChangeColor(metal.change)}`}>
                      {metal.change >= 0 ? '+' : ''}{metal.change?.toFixed(2)}%
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Energy */}
          <Card className="card-dark" data-testid="energy">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2">
                <span className="text-xl">🛢️</span>
                ENERGY
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {marketData?.commodities?.energy?.map((item, idx) => (
                  <div 
                    key={item.symbol}
                    className={`p-4 rounded-lg border ${getChangeBg(item.change)} animate-slide-in`}
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <p className="font-heading font-bold">{item.symbol}</p>
                    <p className="text-xs text-muted-foreground mb-2">{item.name}</p>
                    <p className="font-data font-bold text-2xl">${item.price?.toFixed(2)}</p>
                    <p className={`font-data ${getChangeColor(item.change)}`}>
                      {item.change >= 0 ? '+' : ''}{item.change?.toFixed(2)}%
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CRYPTO TAB */}
        <TabsContent value="crypto" className="mt-6 space-y-6">
          <Card className="card-dark" data-testid="crypto-prices">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading flex items-center gap-2">
                <Bitcoin className="w-5 h-5 text-orange-400" />
                CRYPTOCURRENCY
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {marketData?.crypto?.map((coin, idx) => (
                  <div 
                    key={coin.symbol}
                    className={`p-4 rounded-lg border ${getChangeBg(coin.change)} animate-slide-in`}
                    style={{ animationDelay: `${idx * 50}ms` }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                          coin.symbol === 'BTC' ? 'bg-orange-500/20' :
                          coin.symbol === 'ETH' ? 'bg-blue-500/20' : 'bg-purple-500/20'
                        }`}>
                          <Bitcoin className={`w-6 h-6 ${
                            coin.symbol === 'BTC' ? 'text-orange-400' :
                            coin.symbol === 'ETH' ? 'text-blue-400' : 'text-purple-400'
                          }`} />
                        </div>
                        <div>
                          <p className="font-heading font-bold text-xl">{coin.symbol}</p>
                          <p className="text-sm text-muted-foreground">{coin.name}</p>
                        </div>
                      </div>
                      <Badge className={getChangeBg(coin.change)}>
                        <span className={getChangeColor(coin.change)}>
                          {coin.change >= 0 ? '+' : ''}{coin.change?.toFixed(2)}%
                        </span>
                      </Badge>
                    </div>
                    <p className="font-data font-bold text-3xl">${coin.price?.toLocaleString()}</p>
                    {coin.market_cap && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Market Cap: ${(coin.market_cap / 1e9).toFixed(2)}B
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* NEWS TAB */}
      </Tabs>
    </div>
  );
}
